import { createRootRoute, createRoute, createRouter, lazyRouteComponent } from "@tanstack/react-router";

import { AppLayout } from "@/components/layout";
import type { CatalogFilters } from "@/lib/types";
import { HomePage } from "@/routes/home";

const rootRoute = createRootRoute({
  component: AppLayout,
  notFoundComponent: () => <div className="empty"><h1>Cette pièce a pris un autre chemin.</h1><p>Cette page n'existe pas.</p><a className="button button-dark" href="/catalogue">Explorer les pièces</a></div>,
  errorComponent: ({ reset }) => <div className="empty"><h1>Un contretemps.</h1><p>La page n'a pas pu être chargée.</p><button className="button button-dark" onClick={reset}>Réessayer</button></div>,
});

const indexRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/",
  component: HomePage,
});

function catalogSearch(search: Record<string, unknown>): CatalogFilters {
    const result: CatalogFilters = {};
    const keys = ["q", "kind", "category", "min_price", "max_price", "condition", "location", "sort", "favorites"] as const;
    for (const key of keys) if (typeof search[key] === "string" && search[key]) result[key] = search[key];
    if (search.page && Number(search.page) > 1) result.page = Math.floor(Number(search.page));
    return result;
}

const catalogRoute = createRoute({
  getParentRoute: () => rootRoute, path: "/catalogue",
  validateSearch: catalogSearch,
  component: lazyRouteComponent(() => import("./catalog"), "CatalogPage"),
});
const detailRoute = createRoute({
  getParentRoute: () => rootRoute, path: "/annonces/$slug",
  component: lazyRouteComponent(() => import("./listing"), "ListingPage"),
});
const loginRoute = createRoute({
  getParentRoute: () => rootRoute, path: "/connexion",
  component: lazyRouteComponent(() => import("./auth"), "LoginPage"),
});
const registerRoute = createRoute({
  getParentRoute: () => rootRoute, path: "/inscription",
  component: lazyRouteComponent(() => import("./auth"), "RegisterPage"),
});
const resetRoute = createRoute({
  getParentRoute: () => rootRoute, path: "/reinitialiser",
  component: lazyRouteComponent(() => import("./auth"), "ResetPage"),
});
const accountRoute = createRoute({
  getParentRoute: () => rootRoute, path: "/mon-compte",
  component: lazyRouteComponent(() => import("./account"), "AccountPage"),
});
const depositRoute = createRoute({
  getParentRoute: () => rootRoute, path: "/deposer",
  component: lazyRouteComponent(() => import("./deposit"), "DepositPage"),
});
const favoritesRoute = createRoute({
  getParentRoute: () => rootRoute, path: "/favoris",
  component: lazyRouteComponent(() => import("./catalog"), "FavoritesPage"),
});
const pricingRoute = createRoute({
  getParentRoute: () => rootRoute, path: "/tarifs",
  component: lazyRouteComponent(() => import("./pricing"), "PricingPage"),
});
const moderationRoute = createRoute({
  getParentRoute: () => rootRoute, path: "/moderation",
  component: lazyRouteComponent(() => import("./moderation"), "ModerationPage"),
});
function contentRoute<const T extends string>(path: T) {
  return createRoute({
    getParentRoute: () => rootRoute, path,
    component: lazyRouteComponent(() => import("./content"), "ContentPage"),
  });
}
const contentRoutes = [
  contentRoute("/comment-ca-marche"), contentRoute("/services"), contentRoute("/contact"),
  contentRoute("/faq"), contentRoute("/conditions"), contentRoute("/confidentialite"),
] as const;
const shopRoute = createRoute({ getParentRoute: () => rootRoute, path: "/boutique", validateSearch: catalogSearch, component: lazyRouteComponent(() => import("./shop"), "ShopPage") });
const productRoute = createRoute({ getParentRoute: () => rootRoute, path: "/boutique/$slug", component: lazyRouteComponent(() => import("./shop"), "ProductPage") });
const cartRoute = createRoute({ getParentRoute: () => rootRoute, path: "/panier", component: lazyRouteComponent(() => import("./cart"), "CartPage") });
const checkoutRoute = createRoute({ getParentRoute: () => rootRoute, path: "/checkout", component: lazyRouteComponent(() => import("./checkout"), "CheckoutPage") });
const ordersRoute = createRoute({ getParentRoute: () => rootRoute, path: "/mes-commandes", component: lazyRouteComponent(() => import("./orders"), "OrdersPage") });
const orderRoute = createRoute({ getParentRoute: () => rootRoute, path: "/commandes/$id", component: lazyRouteComponent(() => import("./orders"), "OrderPage") });
const orderManagementRoute = createRoute({ getParentRoute: () => rootRoute, path: "/gestion-commandes", component: lazyRouteComponent(() => import("./order-management"), "OrderManagementPage") });
const routeTree = rootRoute.addChildren([
  shopRoute, productRoute, cartRoute, checkoutRoute, ordersRoute, orderRoute, orderManagementRoute,
  indexRoute, catalogRoute, detailRoute, loginRoute, registerRoute, resetRoute,
  accountRoute, depositRoute, favoritesRoute, pricingRoute, moderationRoute, ...contentRoutes,
]);

export const router = createRouter({
  routeTree,
  defaultPreload: "intent",
  scrollRestoration: true,
});

declare module "@tanstack/react-router" {
  interface Register {
    router: typeof router;
  }
}
